'use client';

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@workspace/ui/components/select';
import { cn } from '@workspace/ui/lib/utils';

import { isLocale, locales, type Locale } from '~/lib/i18n';

function getCurrentLocaleFromPathname(pathname: string): Locale {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return isLocale(first) ? first : 'en';
}

function replaceLocaleInPathname(pathname: string, nextLocale: Locale): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return `/${nextLocale}`;

  if (isLocale(parts[0])) {
    parts[0] = nextLocale;
  } else {
    parts.unshift(nextLocale);
  }

  return `/${parts.join('/')}`;
}

export type LanguageSwitcherProps = {
  className?: string;
  size?: React.ComponentProps<typeof SelectTrigger>['size'];
};

export function LanguageSwitcher({
  className,
  size = 'sm'
}: LanguageSwitcherProps): React.JSX.Element {
  const router = useRouter();
  const pathname = usePathname();
  const current = getCurrentLocaleFromPathname(pathname);

  return (
    <Select
      value={current}
      onValueChange={(value) => {
        const nextLocale = isLocale(value) ? value : current;
        router.push(replaceLocaleInPathname(pathname, nextLocale));
      }}
    >
      <SelectTrigger
        size={size}
        className={cn('rounded-xl', className)}
        aria-label="Select language"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {locales.map((locale) => (
          <SelectItem
            key={locale}
            value={locale}
          >
            {locale.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

